<template>
  <div ref="root" class="multiple-choice-view">
    <cv-form>
      <question-columns-layout :align-two-columns="alignTwoColumns">
        <template #left>
          <!-- Stimulus -->
          <match-indicator v-if="showStimulusMatchIndicator" />
          <dl v-if="stimulus" class="field-set type-text stimulus">
            <dd class="field-value">
              <rich-text-wrapper
                :content="stimulus"
                :expandable="true"
                :expanded="true"
                :can-annotate="canAccessAnnotations"
                :anchor="stimulusAnchor"
                :resource-type="'LESSON_COMPONENT'"
                :annotation-resource-id="questionAnnotationResourceId"
              />
            </dd>
          </dl>
        </template>

        <template #right>
          <!-- Instructions -->
          <dl v-if="!isMultipleChoice" class="field-set type-text instructions">
            <dt class="field-label">
              <svg-icon name="Info16" kind="indicative" />
              <span>{{ t("question_component_label_instructions") }}</span>
            </dt>
            <dd class="field-value">
              {{ t("question_component_label_mrq_instructions_content") }}
            </dd>
          </dl>

          <!-- Question Body -->
          <match-indicator v-if="showTitleMatchIndicator" />
          <dl class="field-set type-text question-body">
            <dd class="field-value">
              <rich-text-wrapper
                :content="questionBody"
                :expandable="true"
                :expanded="true"
                :can-annotate="canAccessAnnotations"
                :anchor="questionBodyAnchor"
                :resource-type="'LESSON_COMPONENT'"
                :annotation-resource-id="questionAnnotationResourceId"
              />
            </dd>
          </dl>

          <!-- Hint -->
          <dl v-if="questionHints && questionHints.length" class="field-set type-text hint">
            <dd class="field-value">
              <AccordionWrapper v-for="(hint, index) in questionHints" :key="hintId(hint, index)">
                <cv-accordion-item :open="true">
                  <template #title>
                    <span class="icon">
                      <svg-icon name="Hint16" kind="indicative" />
                    </span>
                    <span class="title">{{ hintTitle(index) }}</span>
                  </template>
                  <template #content>
                    <rich-text class="hint" :content="hint.description" :expandable="false" />
                  </template>
                </cv-accordion-item>
              </AccordionWrapper>
            </dd>
          </dl>
          <!-- Slider Display Mode -->
          <dl v-if="isSliderDisplayMode" class="field-set">
            <dd class="field-value">
              <base-slider
                :ticks-labels="sliderOptions"
                tick-size="1"
                :choice="sliderAnswerIndex"
                :disabled="true"
              >
                <template #slider-top>
                  <div v-if="isMobile" class="choice">{{ sliderOptions[sliderAnswerIndex] }}</div>
                </template>
              </base-slider>
            </dd>
          </dl>

          <!-- Answer Options -->
          <dl
            class="field-set answer-options"
            :class="{ 'type-radio': isMultipleChoice, 'type-checkbox': !isMultipleChoice }"
          >
            <dd v-for="option in questionOptions" :key="option.id" class="field-value">
              <!-- Multiple Choice/Response Option -->
              <multiple-choice-option-view
                :option="option"
                :is-multiple-choice="isMultipleChoice"
                :is-correct-option="option.answerOption"
                :is-slider-display-mode="isSliderDisplayMode"
                :show-option-mark="showOptionMark"
              />
            </dd>
          </dl>
        </template>
        <template #bottom>
          <div class="feedback-divider"></div>

          <!-- Feedback(Explanation) -->
          <dl v-if="feedback" class="field-set type-text feedback">
            <dd class="field-value">
              <rich-text-card title="Feedback" :content="feedback" truncation-type="secondary">
                <template #icon>
                  <svg-icon name="Feedback32" kind="indicative" />
                </template>
              </rich-text-card>
            </dd>
          </dl>
        </template>
      </question-columns-layout>
    </cv-form>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { useAnnotationPermissions } from "@modules/annotation/composables/use-annotation-permissions.ts";
import DOMUtils from "@utils/dom.utils";
import MultipleChoiceOptionView from "@modules/editor/components/question/MultipleChoiceOptionView.vue";
import { QUESTION_TYPE } from "@modules/editor/service/editor.constant";
import { OPTION_DISPLAY_MODE } from "@modules/forum/service/forum.constant";
import DeviceUtils from "@utils/device.utils";
import { DRAWER_NAME } from "@modules/app/service/app.constant";
import SliderUtils from "@utils/slider.utils.ts";
import AccordionComponentStore from "@plugins/accordion/components/AccordionComponentStore";
import QuestionColumnsLayout from "@/components/QuestionColumnsLayout.vue";
import RichTextWrapper from "@/plugins/richtext/components/RichTextWrapper.vue";
import { useQuestionView } from "@modules/editor/composables/question-view.ts";
import { useQuestionAnnotation } from "@modules/editor/composables/use-question-annotation.ts";
import { type QuestionLayout } from "@/types/question";
import { isEmpty } from "@utils/var.check.ts";

interface Props {
  question: object;
  highlights?: object;
  accordionComponentId: number;
  accordionItemComponentId: number;
}

const props = withDefaults(defineProps<Props>(), {
  highlights: null,
});

const emit = defineEmits<{
  loaded: [questionUuid: string];
}>();

const store = useStore();
const { t } = useI18n();
const { canAccessAnnotations } = useAnnotationPermissions();

// Data
const rootEl = useTemplateRef("root");
const sliderAnswerIndex = ref<number | null>(null);
const drawerName = ref(DRAWER_NAME.SETTINGS);
const accordionComponentStore = ref(AccordionComponentStore);

// Injected dependencies
const resourceContext = inject<{
  isQuestionInDisplay: boolean;
}>("resourceContext");

// Computed
const isPageDrawerExpanded = computed(() =>
  store.getters["app/getPageDrawerExpanded"](drawerName.value),
);

const appearanceSettingsWidth = computed(() => store.getters["app/getAppearanceSettingsWidth"]);

const questionType = computed(() => props.question.questionType);

const isMobile = computed(() => DeviceUtils.isMobile());

const isMultipleChoice = computed(() => questionType.value === QUESTION_TYPE.MCQ);

const isSliderDisplayMode = computed(
  () => props.question.optionDisplayMode === OPTION_DISPLAY_MODE.SLIDER.value,
);

const questionBody = computed(() => props.question.questionBody);
const stimulus = computed(() => props.question?.stimulus ?? "");
const { questionBodyAnchor, stimulusAnchor, questionAnnotationResourceId } =
  useQuestionAnnotation();

const questionHints = computed(() => props.question.questionHintModels);

const hintTitle = computed(() => (index: number) => {
  return `${t("question_component_label_hint")} ${index + 1}`;
});

const feedback = computed(() => props.question.explanation);

const questionOptions = computed(() => props.question.questionOptionModels);

const sliderOptions = computed(() =>
  Object.assign(
    {},
    questionOptions.value.map((option: object) => option.answer),
  ),
);

const hintId = computed(() => (hint: object, index: number) => {
  return hint.id ? hint.id.toString() : index.toString();
});

const showOptionMark = computed(() => props.question.markByOption);
const isAccordionForComponentOpened = computed(() =>
  accordionComponentStore.value.getAccordionItemState(
    props.accordionComponentId,
    props.accordionItemComponentId,
  ),
);

const showTitleMatchIndicator = computed(() => !isEmpty(props.question.highlights?.title));

const showStimulusMatchIndicator = computed(() => !isEmpty(props.question.highlights?.stimulus));

const { alignTwoColumns } = useQuestionView({
  question: props.question as { parentQuestionUuid?: string; layout?: QuestionLayout },
  resourceContext: resourceContext,
});

// Watchers
watch(isAccordionForComponentOpened, () => {
  if (!isAccordionForComponentOpened.value) {
    return;
  }
  SliderUtils.setSliderCSSVar(rootEl.value);
});

watch(isPageDrawerExpanded, () => {
  SliderUtils.setSliderCSSVar(rootEl.value);
});

watch(appearanceSettingsWidth, () => {
  SliderUtils.setSliderCSSVar(rootEl.value);
});

// Methods
const initSliderAnswerIndex = () => {
  // set null if no answer
  sliderAnswerIndex.value =
    questionOptions.value?.find((option: object) => option.answerOption)?.indexNo ?? null;
};

const initSliderResultElements = () => {
  const component = DOMUtils.elOf(rootEl.value, ".v-slider-track__ticks");
  Array.from(component[0].children).forEach((option, index) => {
    if (index === sliderAnswerIndex.value) {
      DOMUtils.addClass(option, "correct-option");
    }
  });
};

// Lifecycle
onMounted(() => {
  emit("loaded", props.question.uuid);

  if (isSliderDisplayMode.value) {
    nextTick(() => {
      initSliderResultElements();
      SliderUtils.setSliderCSSVar(rootEl.value);
    });
  }
});

// Initialize
if (isSliderDisplayMode.value) {
  initSliderAnswerIndex();
}
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";

.multiple-choice-view {
  .sls-slider {
    margin-bottom: var(--slider-margin-bottom, 2rem);

    @include media("mobile") {
      display: flex;
      flex-flow: row wrap;
      .v-slider {
        width: 100%;
      }
    }

    .v-slider-thumb {
      display: none;
    }

    .v-slider-track__tick {
      &.correct-option {
        background-color: transparent;

        &::before,
        &::after {
          content: "";
          position: absolute;
        }

        .v-slider-track__tick-label {
          color: $text-01;
        }
      }

      .answer-options {
        border-bottom: 1px solid $ui-03;
      }

      &.correct-option {
        &::before {
          top: -14px;
          left: -6px;
          height: 16px;
          width: 16px;
          background: url("/src/assets/images/icons/indicative/16px/Checkmark16.svg");
          z-index: z-map("default");
          filter: invert(1) sepia(1) hue-rotate(90deg) brightness(0.7);
        }

        &::after {
          top: -1rem;
          left: -0.5rem;
          height: 20px;
          width: 20px;
          background-color: $ui-01;
          border: 1px solid $state-positive;
          border-radius: 100%;
        }
      }
    }
  }

  .field-set {
    &.answer-options {
      margin-bottom: 0;
    }
  }

  .feedback-divider {
    height: 1px;
    width: 100%;
    background-color: $ui-03;
  }

  .rich-text-actions {
    margin-top: $spacing-02;
  }

  .question-two-columns {
    margin-bottom: $spacing-05;
  }
}
</style>
